import React, { useCallback } from 'react';
import Button from '@atlaskit/button/new';
import EmptyState from '@atlaskit/empty-state';
import { FormattedMessage, useIntl } from 'react-intl-next';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import { useEmbeddedPage } from '../../../containers/embedded-page';
import errorImage from '../../../view/empty-state/icons/error.svg';
import { useCreateEmbeddedPage } from '../../../containers/embedded-page/utils';
import messages from './messages';
import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import { type Props } from './types';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	height: '100%',
	alignItems: 'center',
});

const CreatePageError = ({ spaceKey, onCloseModal, projectId, projectType }: Props) => {
	const { formatMessage } = useIntl();
	const { pageCreate } = useEmbeddedPage();
	const handleClose = useCallback(
		(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			onCloseModal();
			fireUIAnalytics(analyticsEvent, 'createEmbeddedPageErrorCloseButton', {
				embeddedPageAction: true,
			});
		},
		[onCloseModal],
	);
	const createEmbeddedPage = useCreateEmbeddedPage({
		spaceKey,
		projectId,
		projectType,
	});
	const handleTryAgain = useCallback(
		async (_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			if (pageCreate) {
				await createEmbeddedPage(
					pageCreate.parentContentId,
					pageCreate.isRoot,
					pageCreate.blueprint,
				);
			}
			fireUIAnalytics(analyticsEvent, 'createEmbeddedPageErrorTryAgainButton', {
				embeddedPageAction: true,
			});
		},
		[createEmbeddedPage, pageCreate],
	);
	return (
		<Wrapper data-testid="embedded-confluence-error-page">
			<EmptyState
				imageUrl={errorImage}
				imageWidth={71}
				header={formatMessage(messages.title)}
				description={<FormattedMessage {...messages.description} />}
				primaryAction={
					<Button appearance="primary" onClick={handleTryAgain}>
						{formatMessage(messages.tryAgain)}
					</Button>
				}
				secondaryAction={<Button onClick={handleClose}>{formatMessage(messages.close)}</Button>}
			/>
		</Wrapper>
	);
};

export default CreatePageError;
